import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import {
  fetchNotificationPreferences,
  saveNotificationPreferences,
} from "../../Apis/Notifications-api";
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  TYPE_LABELS,
} from "../../Utils/notificationPreferencesDefaults";
import toast from "react-hot-toast";

export default function NotificationPreferencesDialog({
  open,
  onClose,
  organizationId,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState([]);

  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setLoadError("No active organization. Switch or select an organization first.");
      setPreferences([]);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchNotificationPreferences(organizationId);
      const list = Array.isArray(data) ? data : [];
      setPreferences(
        list.length > 0 ? list : DEFAULT_NOTIFICATION_PREFERENCES.map((p) => ({ ...p }))
      );
    } catch (err) {
      const status = err?.response?.status;
      let message = "Failed to load notification preferences.";
      if (status === 404) {
        message =
          "Notification API is not on this server (404). Point MAIN_URL to your local backend or deploy the latest API.";
      } else if (status === 422) {
        message = err?.response?.data?.message || "organization_id is required.";
      } else if (err?.response?.data?.message) {
        message = String(err.response.data.message);
      }
      setLoadError(message);
      setPreferences(DEFAULT_NOTIFICATION_PREFERENCES.map((p) => ({ ...p })));
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (open) {
      load();
    }
  }, [open, load]);

  const updatePref = (type, field, value) => {
    setPreferences((prev) =>
      prev.map((p) => (p.type === type ? { ...p, [field]: value } : p))
    );
  };

  const handleSave = async () => {
    if (!organizationId) return;
    setSaving(true);
    try {
      const data = await saveNotificationPreferences(
        organizationId,
        preferences.map((p) => ({
          type: p.type,
          in_app_enabled: !!p.in_app_enabled,
          email_enabled: !!p.email_enabled,
        }))
      );
      setPreferences(Array.isArray(data) ? data : preferences);
      toast.success("Notification preferences saved");
      onClose?.();
    } catch {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Notification preferences</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {loadError && (
              <Typography color="error" fontSize={13} sx={{ mb: 2 }}>
                {loadError}
              </Typography>
            )}
            {preferences.map((pref, index) => (
            <Box key={pref.type} sx={{ py: 1.25 }}>
              {index > 0 && <Divider sx={{ mb: 1.5 }} />}
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                {TYPE_LABELS[pref.type] || pref.type}
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!pref.in_app_enabled}
                      onChange={(e) =>
                        updatePref(pref.type, "in_app_enabled", e.target.checked)
                      }
                      size="small"
                    />
                  }
                  label="In-app"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!pref.email_enabled}
                      onChange={(e) =>
                        updatePref(pref.type, "email_enabled", e.target.checked)
                      }
                      size="small"
                    />
                  }
                  label="Email"
                />
              </Box>
            </Box>
          ))}
          </>
        )}
        {!loading && preferences.length === 0 && !loadError && (
          <Typography color="text.secondary" fontSize={13}>
            No preference types available.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || saving}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
